import { useEffect, useRef, useState } from 'react';
import { SECTION_META, isSectionVisible } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { useAnalyticsTracking } from '../state/analytics.js';
import { useSession } from '../state/session.js';
import { useUI } from '../state/ui.js';
import { Icon } from './Icon.js';
import { SECTION_ICONS } from './Icon.js';

/** Glassy section switcher shown as a compact dropdown in the top bar. */
export function SectionTabs() {
  const { section, setSection } = useUI();
  const { isAdmin } = useSession();
  const { t } = useI18n();
  const analytics = useAnalyticsTracking();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const visibleSections = SECTION_META.filter((meta) => isSectionVisible(meta.id, isAdmin));
  const fallbackSection = visibleSections[0] ?? SECTION_META[0]!;
  const activeSection = visibleSections.find((meta) => meta.id === section) ?? fallbackSection;

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  return (
    <div className={`section-tabs${isOpen ? ' is-open' : ''}`} ref={rootRef} data-no-swipe>
      <button
        type="button"
        className="section-tab-trigger"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={`${t(activeSection.labelKey as Parameters<typeof t>[0])} — ${t('app.name')}`}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((value) => !value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen((v) => !v);
          }
        }}
      >
        <Icon name={SECTION_ICONS[activeSection.id] ?? 'chat'} size={18} />
        <span className="section-tab-trigger-label">
          {t(activeSection.labelKey as Parameters<typeof t>[0])}
        </span>
        <Icon name={isOpen ? 'chevronUp' : 'chevronDown'} size={16} />
      </button>

      <div
        className={`section-tab-dropdown${isOpen ? ' is-open' : ''}`}
        role="menu"
        aria-label="Sections"
      >
        {visibleSections.map((meta) => {
          const active = section === meta.id;
          return (
            <button
              key={meta.id}
              type="button"
              role="menuitemradio"
              aria-checked={active}
              className={`section-tab-option${active ? ' is-active' : ''}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => {
                analytics.trackSectionClick(meta.id);
                setSection(meta.id);
                setIsOpen(false);
              }}
            >
              <Icon name={SECTION_ICONS[meta.id] ?? 'chat'} size={18} />
              <span className="section-tab-label">
                {t(meta.labelKey as Parameters<typeof t>[0])}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
